import * as React from "react";
import { Checkbox, ICheckboxStyles } from "@fluentui/react";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormGroupValidationModeContext,
  FormInputGroupLayout,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

export interface CheckBoxProps extends BaseInputProps {
  /**
   * Label option for the checkbox
  */
  label?: string;
  /**
   * Default checked or unchecked option
  */
  defaultValue?: boolean;
  /**
   * Option to display checkbox before or after the label
  */
  switchCheckBoxSide?: boolean;
  /**
   * Callback when the checkbox is checked or unchecked
  */
  onChange?: (checked: boolean) => void;
  /**
   * Callback to validate the checkbox
  */
  validator?: (value: boolean) => string[] | undefined;
}

const checkBoxStyles: ICheckboxStyles = { root: { width: "100%", marginTop: "5px !important" } };
const checkBoxDisabledUncheckedStyles: ICheckboxStyles = {
  root: { width: "100%", marginTop: "5px !important" },
  checkbox: { background: "#f3f2f1" },
};

const checkBoxSwitchedStyles: ICheckboxStyles = { root: { marginTop: "2px" } };
const checkBoxDisabledUncheckedSwitchedStyles: ICheckboxStyles = {
  root: { marginTop: "2px" },
  checkbox: { background: "#f3f2f1" },
};

export type CheckBoxTestIds = ComponentTestIds & LabelWrapperTestIds;
export const buildCheckBoxTestIds = (testId?: string): CheckBoxTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  component: buildTestId(testId),
});

/**
 *The Checkbox component provides a way to select one or more items from a group,
 *or switch between two mutually exclusive options (checked or unchecked, on or off)
 *for the specified field.
 */

export const CheckBox = ({
  testId,
  fieldName,
  groupName,
  label,
  defaultValue,
  tooltip,
  required,
  statusInfo,
  subField,
  disabled,
  switchCheckBoxSide,
  onChange,
  validator,
}: CheckBoxProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();
  if (!Object.keys(form).length) {
    throw new Error("CheckBox should be used within form");
  }
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const [isChecked, setIsChecked] = React.useState<boolean>(false);

  const checkboxTestIds = buildCheckBoxTestIds(testId);

  React.useEffect(() => {
    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (validator) {
      form.registerField(fieldName, group);
    }

    if (defaultValue) {
      setIsChecked(defaultValue);
      form.setValue(defaultValue, fieldName, group);
      fieldValidation();
    } else form.setValue(isChecked, fieldName, group);

    return function cleanup() {
      if (validator) {
        form.unRegisterField(fieldName, group);
      }
    };
  // Only at mount time. Others props should not have side effect beyond their initial values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldValidation = () : void => {
    const errorMsg = validator?.(form.getValue(fieldName, group));
    const errors = [...errorMsg || []];
    form.setFieldState(
      errors.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  const internalOnChange = (
    _?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean,
  ): void => {
    if (checked !== undefined) { setIsChecked(checked); }
    form.setValue(checked, fieldName, group);
    if (validator) {
      fieldValidation();
    }
    onChange?.(checked || false);
  };

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (validator) {
        fieldValidation();
      }
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  return (
    <LabelWrapper
      horizontal={layout === FormInputGroupLayout.WIDE || switchCheckBoxSide}
      reversed={switchCheckBoxSide}
      errors={errorMessage || []}
      fieldName={fieldName}
      layout={layout}
      label={label}
      required={required}
      statusInfo={statusInfo}
      subField={subField}
      tooltip={tooltip}
      testId={testId}
    >
      <Checkbox
        id={fieldName}
        inputProps={{ "data-test-id": checkboxTestIds.component } as Record<string, string | undefined>}
        onChange={internalOnChange}
        disabled={disabled}
        checked={isChecked}
        styles={
        switchCheckBoxSide
          ? disabled && !isChecked ? checkBoxDisabledUncheckedSwitchedStyles : checkBoxSwitchedStyles
          : disabled && !isChecked ? checkBoxDisabledUncheckedStyles : checkBoxStyles
      }
      />
    </LabelWrapper>
  );
};
